import React from "react";
import { useNavigate } from "react-router-dom";
import { Minimize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { useMatches, useOfficialMatch, useUserMatches } from "@/queries/matches";
import TeamLogo from "@/components/TeamLogo";
import ProtocolMatchesPicker from "@/components/protocol/ProtocolMatchesPicker";
import ProtocolOfficialMatchView from "@/components/protocol/ProtocolOfficialMatchView";
import MatchProtocolPage from "@/pages/MatchProtocolPage";
import MatchesPage from "@/pages/MatchesPage";
import {
  findExistingProtocolForOfficialMatch,
  findSavedProtocolForOfficialMatch,
} from "@/lib/protocolMatches";
import { getNativePlatform } from "@/lib/runtime";
import { preventNativeOnboardingDismiss } from "@/lib/nativeOnboarding";
import NativeProtocolStartMenu from "@/components/native/NativeProtocolStartMenu";

function MatchIdentityRow({ homeTeam, awayTeam }) {
  return (
    <div className="native-v3-protocol-identity rounded-xl border border-border bg-card/70 px-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <TeamLogo teamName={homeTeam} size={28} className="h-7 w-7 shrink-0 object-contain" />
          <span className="truncate text-sm font-medium text-foreground">{homeTeam || "Hemmalag"}</span>
        </div>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">vs</span>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          <span className="truncate text-sm font-medium text-foreground">{awayTeam || "Bortalag"}</span>
          <TeamLogo teamName={awayTeam} size={28} className="h-7 w-7 shrink-0 object-contain" />
        </div>
      </div>
    </div>
  );
}

export default function ProtocolShell() {
  const navigate = useNavigate();
  const {
    isOpen,
    openShell,
    closeShell,
    setIsOpen,
    openOfficialShell,
    selectedOfficialId,
    activeProtocolMatchId,
    hasActiveProtocol,
    requestProtocolSession,
    minimizeProtocolSession,
    clearProtocolSession,
    resumeToken,
    pendingSwitchMatchId,
    switchPromptOpen,
    confirmProtocolSwitch,
    cancelProtocolSwitch,
    pendingOfficialId,
    officialSwitchPromptOpen,
    confirmOfficialSwitch,
    cancelOfficialSwitch,
    resumeProtocolSession,
    menuToken,
  } = useProtocolShell();
  const { data: createdMatches = [] } = useMatches({ staleTime: Infinity });
  const { data: savedUserMatches = [] } = useUserMatches({ staleTime: Infinity });
  const { data: pendingOfficialMatch } = useOfficialMatch(pendingOfficialId, {
    enabled: !!pendingOfficialId,
    staleTime: 5 * 60 * 1000,
  });

  const [view, setView] = React.useState("list");
  const [confirmCloseOpen, setConfirmCloseOpen] = React.useState(false);
  const [isLargeScreen, setIsLargeScreen] = React.useState(false);
  const handledMenuTokenRef = React.useRef(menuToken);
  const isNativeAndroid = getNativePlatform() === "android";

  const isIOS = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    const platform = window.navigator.platform || "";
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;
    return /iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && maxTouchPoints > 1);
  }, []);

  const activeProtocolMatch = React.useMemo(
    () =>
      (Array.isArray(createdMatches) ? createdMatches : []).find(
        (match) => String(match?.id || "") === String(activeProtocolMatchId || "")
      ) || null,
    [createdMatches, activeProtocolMatchId]
  );

  const pendingProtocolMatch = React.useMemo(
    () =>
      (Array.isArray(createdMatches) ? createdMatches : []).find(
        (match) => String(match?.id || "") === String(pendingSwitchMatchId || "")
      ) || null,
    [createdMatches, pendingSwitchMatchId]
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setIsLargeScreen(media.matches);
    update();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  const openMatches = React.useCallback(() => {
    setView("all");
  }, []);

  const openOfficial = React.useCallback(
    (officialId) => {
      if (!officialId) return;
      const savedProtocol = findSavedProtocolForOfficialMatch(savedUserMatches, officialId);
      if (savedProtocol?.id) {
        navigate(`/account/matches/saved/${savedProtocol.id}`);
        return;
      }
      const existingMatch = findExistingProtocolForOfficialMatch(createdMatches, officialId);
      if (existingMatch?.id) {
        requestProtocolSession(existingMatch.id);
        return;
      }
      openOfficialShell(officialId);
    },
    [createdMatches, savedUserMatches, navigate, openOfficialShell, requestProtocolSession]
  );

  React.useEffect(() => {
    if (!isOpen) return;
    if (selectedOfficialId || activeProtocolMatchId) return;
    setView("list");
  }, [isOpen, selectedOfficialId, activeProtocolMatchId]);

  React.useEffect(() => {
    if (!isOpen || !selectedOfficialId) return;
    setView("official");
  }, [isOpen, selectedOfficialId]);

  React.useEffect(() => {
    if (!isOpen || !activeProtocolMatchId) return;
    setView("protocol");
  }, [isOpen, activeProtocolMatchId, resumeToken]);

  React.useEffect(() => {
    if (!isOpen || !menuToken || handledMenuTokenRef.current === menuToken) return;
    handledMenuTokenRef.current = menuToken;
    setView("list");
  }, [isOpen, menuToken]);

  const leaveShellFor = React.useCallback(
    (path) => {
      closeShell();
      navigate(path);
    },
    [closeShell, navigate]
  );

  const handleRequestClose = React.useCallback(() => {
    if (hasActiveProtocol) {
      setConfirmCloseOpen(true);
      return;
    }
    closeShell();
  }, [hasActiveProtocol, closeShell]);

  const handleConfirmClose = React.useCallback(() => {
    setConfirmCloseOpen(false);
    clearProtocolSession();
  }, [clearProtocolSession]);

  const handleShellOpenChange = React.useCallback(
    (next) => {
      if (next) {
        setIsOpen(true);
        return;
      }
      if (hasActiveProtocol) {
        minimizeProtocolSession();
        return;
      }
      closeShell();
    },
    [closeShell, hasActiveProtocol, minimizeProtocolSession, setIsOpen]
  );

  const shellSubtitle =
    view === "official"
      ? "Matchkort"
      : view === "protocol"
        ? "Pågående protokoll"
        : view === "all"
          ? "Alla matcher"
          : "Starta eller fortsätt protokoll";

  const renderShellContent = (desktop = false) => (
    <>
      <div className={`relative border-b border-border px-4 py-3 text-left ${isNativeAndroid ? "native-v3-protocol-header" : ""}`}>
        <div>
          <DrawerTitle className="text-lg">
            {isNativeAndroid && view === "list" ? "Vad vill du göra?" : "Protokoll-läge"}
          </DrawerTitle>
          {isNativeAndroid && view === "list" ? null : <DrawerDescription>{shellSubtitle}</DrawerDescription>}
        </div>

        {view === "all" || view === "picker" ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setView(view === "all" && isNativeAndroid ? "picker" : "list")}
            className="absolute right-12 top-2"
          >
            Tillbaka
          </Button>
        ) : null}

        {view === "protocol" && hasActiveProtocol ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={minimizeProtocolSession}
            className="absolute right-12 top-2"
          >
            <Minimize2 className="mr-1 h-4 w-4" />
            Minimera
          </Button>
        ) : null}

        {!desktop ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRequestClose}
            className="absolute right-2 top-2"
          >
            <X className="h-5 w-5" />
          </Button>
        ) : null}
      </div>

      <div
        data-protocol-scroll-area={view === "protocol" ? "true" : "false"}
        className={
          view === "protocol"
            ? `${isIOS ? "protocol-shell-scroll-ios " : ""}overflow-y-auto overscroll-contain p-2 ${isNativeAndroid ? "pb-2 native-v3-protocol-running-content" : "pb-24"} md:p-4`
            : `${isIOS ? "protocol-shell-scroll-ios " : ""}space-y-3 overflow-y-auto overscroll-contain p-4 pb-24 ${isNativeAndroid ? "native-v3-protocol-content" : ""}`
        }
      >
        {view === "official" && selectedOfficialId ? (
          <ProtocolOfficialMatchView
            officialId={selectedOfficialId}
            onBack={() => {
              setView("list");
              openShell();
            }}
            onStartProtocol={(matchId) => {
              requestProtocolSession(matchId);
              setView("protocol");
            }}
          />
        ) : view === "all" ? (
          <MatchesPage embedded onOpenOfficial={openOfficial} />
        ) : view === "protocol" && activeProtocolMatchId ? (
          <MatchProtocolPage
            embedded
            matchId={activeProtocolMatchId}
            onExit={clearProtocolSession}
            embeddedFooterId={isNativeAndroid ? "protocolShellPrimaryFooter" : null}
          />
        ) : isNativeAndroid && view === "list" ? (
          <NativeProtocolStartMenu
            activeMatch={activeProtocolMatch}
            onStart={() => setView("picker")}
            onResume={resumeProtocolSession}
            onCreated={() => leaveShellFor("/account/matches/created-protocols")}
            onSaved={() => leaveShellFor("/account/matches/saved-protocols")}
            createdCount={Array.isArray(createdMatches) ? createdMatches.length : 0}
            savedCount={Array.isArray(savedUserMatches) ? savedUserMatches.length : 0}
          />
        ) : (
          <ProtocolMatchesPicker onOpenOfficial={openOfficial} onOpenAll={openMatches} />
        )}
      </div>
      {isNativeAndroid && view === "protocol" ? (
        <div
          id="protocolShellPrimaryFooter"
          data-vaul-no-drag=""
          className="native-v3-protocol-footer-slot shrink-0"
        />
      ) : null}
    </>
  );

  return (
    <>
      {isLargeScreen ? (
        <Dialog open={isOpen} onOpenChange={handleShellOpenChange}>
          <DialogContent
            className={[
              "flex flex-col gap-0 overflow-hidden p-0",
              view === "protocol"
                ? "h-[92vh] max-w-[min(1180px,calc(100vw-2rem))]"
                : "h-[88vh] max-w-[min(1040px,calc(100vw-2rem))]",
            ].join(" ")}
          >
            {renderShellContent(true)}
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer
          open={isOpen}
          onOpenChange={handleShellOpenChange}
          shouldScaleBackground={false}
          {...(isIOS
            ? {
                closeThreshold: 0.4,
                scrollLockTimeout: 700,
                fixed: true,
              }
            : {})}
        >
          <DrawerContent
            data-native-protocol={isNativeAndroid ? "true" : "false"}
            data-native-protocol-view={isNativeAndroid ? view : undefined}
            onPointerDownOutside={preventNativeOnboardingDismiss}
            onInteractOutside={preventNativeOnboardingDismiss}
            className={[
              "pb-0 transform-gpu will-change-transform overscroll-contain [backface-visibility:hidden] [-webkit-backface-visibility:hidden]",
              isIOS && "protocol-shell-drawer-ios",
              view === "protocol"
                ? "mt-0 h-[100dvh] max-h-[100dvh] rounded-none pt-[env(safe-area-inset-top)]"
                : "max-h-[92dvh]",
            ].join(" ")}
          >
            {renderShellContent(false)}
          </DrawerContent>
        </Drawer>
      )}

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent className={isNativeAndroid ? "native-v3-protocol-alert" : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Stäng protokoll-läge?</AlertDialogTitle>
            <AlertDialogDescription>
              Pågående protokoll stängs. Matchen finns kvar i dina skapade protokoll för att återuppta senare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="native-v3-protocol-alert-footer">
            <AlertDialogCancel className="native-v3-protocol-alert-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="native-v3-protocol-alert-primary" onClick={handleConfirmClose}>Stäng protokoll</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={switchPromptOpen} onOpenChange={(open) => !open && cancelProtocolSwitch()}>
        <AlertDialogContent className={isNativeAndroid ? "native-v3-protocol-alert" : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Byta pågående matchprotokoll?</AlertDialogTitle>
            <AlertDialogDescription>
              Du har redan ett pågående matchprotokoll. Vill du återuppta ditt pågående protokoll eller byta till det nya?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            {activeProtocolMatch ? (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Pågående protokoll
                </p>
                <MatchIdentityRow
                  homeTeam={activeProtocolMatch.home_team}
                  awayTeam={activeProtocolMatch.away_team}
                />
              </div>
            ) : null}
            {pendingProtocolMatch ? (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Nytt protokoll
                </p>
                <MatchIdentityRow
                  homeTeam={pendingProtocolMatch.home_team}
                  awayTeam={pendingProtocolMatch.away_team}
                />
              </div>
            ) : null}
          </div>
          <AlertDialogFooter className="native-v3-protocol-alert-footer native-v3-protocol-alert-switch-footer gap-2 sm:gap-2">
            <AlertDialogAction className="native-v3-protocol-alert-primary" onClick={confirmProtocolSwitch}>Byt protokoll</AlertDialogAction>
            <AlertDialogAction
              className="native-v3-protocol-alert-secondary bg-secondary text-secondary-foreground hover:bg-secondary/80"
              onClick={() => {
                cancelProtocolSwitch();
                resumeProtocolSession();
              }}
            >
              Återuppta protokoll
            </AlertDialogAction>
            <AlertDialogCancel className="native-v3-protocol-alert-cancel" onClick={cancelProtocolSwitch}>Avbryt</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={officialSwitchPromptOpen} onOpenChange={(open) => !open && cancelOfficialSwitch()}>
        <AlertDialogContent className={isNativeAndroid ? "native-v3-protocol-alert" : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Du har ett pågående matchprotokoll</AlertDialogTitle>
            <AlertDialogDescription>
              Vill du öppna valt matchkort eller återuppta ditt pågående protokoll?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            {activeProtocolMatch ? (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Pågående protokoll
                </p>
                <MatchIdentityRow
                  homeTeam={activeProtocolMatch.home_team}
                  awayTeam={activeProtocolMatch.away_team}
                />
              </div>
            ) : null}
            {pendingOfficialMatch ? (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Valt matchkort
                </p>
                <MatchIdentityRow
                  homeTeam={pendingOfficialMatch.home_team_display || pendingOfficialMatch.home_team}
                  awayTeam={pendingOfficialMatch.away_team_display || pendingOfficialMatch.away_team}
                />
              </div>
            ) : null}
          </div>
          <AlertDialogFooter className="native-v3-protocol-alert-footer native-v3-protocol-alert-switch-footer gap-2 sm:gap-2">
            <AlertDialogAction className="native-v3-protocol-alert-primary" onClick={confirmOfficialSwitch}>
              Öppna matchkort
            </AlertDialogAction>
            <AlertDialogAction
              className="native-v3-protocol-alert-secondary bg-secondary text-secondary-foreground hover:bg-secondary/80"
              onClick={() => {
                cancelOfficialSwitch();
                resumeProtocolSession();
              }}
            >
              Återuppta protokoll
            </AlertDialogAction>
            <AlertDialogCancel className="native-v3-protocol-alert-cancel" onClick={cancelOfficialSwitch}>Avbryt</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
